import { useEffect, useRef, useState } from 'react';
import { useLocaleContext } from '../../../context';
import type { DisplayProperties } from '../../../utils';
import { getLanguageFromLocale, localeToUpperCase } from '../../../utils';
import { newRelicAvailabilityChecker } from '../../../utils/newrelic';
import { Skeleton } from '../../skeleton';
import TrustpilotScript from '../TrustpilotScript';
import { trustpilotBusinessUnitIds } from '../utils/trustpilot.config';

export type TrustpilotWidgetProps = {
  // eslint-disable-next-line react/no-unused-prop-types
  displayProperties?: DisplayProperties;
  componentData: {
    templateId: string;
    styleHeight: string;
    styleWidth: string;
    stars?: string;
    minReviewCount?: number;
    sku?: string;
    reviewLanguages?: string;
    tags?: string;
    noReviews?: string;
    thirdPartyReviews?: boolean;
    fullwidth?: boolean;
    scrollToList?: boolean;
    productName?: string;
    schemaType?: string;
    fontFamily?: string;
    textColor?: string;
    borderColor?: string;
    quoteColor?: string;
    starColor?: string;
    linkColor?: string;
    buttonColor?: string;
    buttonTextColor?: string;
    alignment?: string;
    theme?: string;
  };
};

export function TrustpilotWidget({ componentData }: TrustpilotWidgetProps) {
  const {
    templateId,
    styleHeight,
    styleWidth,
    stars,
    minReviewCount,
    sku,
    tags,
    noReviews,
    thirdPartyReviews,
    fullwidth,
    scrollToList,
    productName,
    schemaType,
    fontFamily,
    textColor,
    borderColor,
    quoteColor,
    starColor,
    linkColor,
    buttonColor,
    buttonTextColor,
    alignment,
    theme,
  } = componentData;
  const { locale } = useLocaleContext();
  const { buid } = trustpilotBusinessUnitIds;
  const widgetRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const handleScriptLoad = () => {
    setIsLoading(false);
  };

  const handleScriptError = (error: Error) => {
    console.error('Failed to load Trustpilot script:', error);
    newRelicAvailabilityChecker(() => {
      window.newrelic.noticeError(new Error(`Failed to load Trustpilot script: ${error}`));
    });
    setHasError(true);
    setIsLoading(false);
  };

  // Check if script is already loaded from browser cache
  useEffect(() => {
    if (window.Trustpilot) {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hasError || isLoading || !window.Trustpilot || !widgetRef.current || !buid) {
      return;
    }

    try {
      window.Trustpilot.loadFromElement(widgetRef.current, true);
    } catch (error) {
      console.error('Failed to load Trustpilot widget:', error);
      newRelicAvailabilityChecker(() => {
        window.newrelic.noticeError(new Error(`Failed to load Trustpilot widget: ${error}`));
      });
      setHasError(true);
    }
  }, [isLoading, hasError, buid]);

  const skeletonWidth = styleWidth.includes('%') ? undefined : parseInt(styleWidth, 10);
  const skeletonHeight = styleHeight.includes('%') ? undefined : parseInt(styleHeight, 10);

  return (
    <>
      <TrustpilotScript onLoad={handleScriptLoad} onError={handleScriptError} />
      {!hasError && (
        <Skeleton skeletonWidth={skeletonWidth} skeletonHeight={skeletonHeight} isLoading={isLoading}>
          <div
            ref={widgetRef}
            className="trustpilot-widget"
            data-locale={localeToUpperCase(locale)}
            data-review-languages={getLanguageFromLocale(locale)}
            data-businessunit-id={buid}
            data-template-id={templateId}
            data-style-height={styleHeight}
            data-style-width={styleWidth}
            data-stars={stars}
            data-min-review-count={minReviewCount}
            data-sku={sku}
            data-tags={tags}
            data-no-reviews={noReviews}
            data-third-party-reviews={thirdPartyReviews?.toString()}
            data-fullwidth={fullwidth?.toString()}
            data-scroll-to-list={scrollToList?.toString()}
            data-name={productName}
            data-schema-type={schemaType}
            data-font-family={fontFamily}
            data-text-color={textColor}
            data-border-color={borderColor}
            data-quote-color={quoteColor}
            data-star-color={starColor}
            data-link-color={linkColor}
            data-button-color={buttonColor}
            data-button-text-color={buttonTextColor}
            data-style-alignment={alignment}
            data-theme={theme}
          />
        </Skeleton>
      )}
    </>
  );
}
